/********************************************************************
 * ADOBE CONFIDENTIAL
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { OrderDataModel } from '@/order/data/models';
import {
  transformOrderContext,
  transformShoppingCartContext,
} from '@/order/data/transforms';

/**
 * See: https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-sdk/src/contexts.ts
 */
export const contexts = {
  SHOPPING_CART_CONTEXT: 'shoppingCartContext',
  ORDER_CONTEXT: 'orderContext',
  CHANNEL_CONTEXT: 'channelContext',
  PERSONAL_EMAIL_CONTEXT: 'personalEmail',
};

/**
 * See: https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-sdk/src/events.ts
 */
export const events = {
  // See: https://github.com/adobe/commerce-events/blob/main/examples/events/place-order.md
  PLACE_ORDER: 'place-order',
};

export function getAdobeDataLayer() {
  // @ts-ignore
  window.adobeDataLayer = window.adobeDataLayer || [];
  // @ts-ignore
  return window.adobeDataLayer;
}

/**
 * Sets a context in the Adobe Client Data Layer (ACDL)
 * Logic based on: https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-sdk/src/Base.ts#L6
 */
function setContext(name: string, data: any) {
  const adobeDataLayer = getAdobeDataLayer();

  // Clear existing context
  adobeDataLayer.push({
    [name]: null,
  });

  // Set new context
  adobeDataLayer.push({
    [name]: data,
  });
}

/**
 * Pushes an event to the Adobe Client Data Layer (ACDL)
 * Logic based on: https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-sdk/src/Base.ts#L35
 */
function pushEvent(event: string) {
  const adobeDataLayer = getAdobeDataLayer();

  adobeDataLayer.push((acdl: any) => {
    const state = acdl.getState ? acdl.getState() : {};

    acdl.push({
      event,
      eventInfo: {
        ...state,
      },
    });
  });
}

function getChannelContext() {
  return {
    _id: "https://ns.adobe.com/xdm/channels/web",
    _type: "https://ns.adobe.com/xdm/channel-types/web",
  };
}

export function setChannelContext() {
  setContext(contexts.CHANNEL_CONTEXT, getChannelContext());
}

// Triggered when an order is placed
export function publishPlaceOrderEvent(cartId: string, data: OrderDataModel) {
  const orderContext = transformOrderContext(data);
  const shoppingCartContext = transformShoppingCartContext(cartId, data);

  // Set context and push open cart event
  setContext(contexts.ORDER_CONTEXT, {
    ...orderContext,
  });

  setContext(contexts.SHOPPING_CART_CONTEXT, {
    ...shoppingCartContext,
  });

  setContext(contexts.PERSONAL_EMAIL_CONTEXT, {
    address: data.email,
  });

  setChannelContext();

  pushEvent(events.PLACE_ORDER);
}
